'use client';

import type { FC } from 'react';
import type { ToastProps } from '@paypoteam/ui-components';
import {
    Button,
    buttonVariants,
    Card,
    P,
    Title,
    useToast,
} from '@paypoteam/ui-components';

import ShowMoreContent from './ShowMoreContent';

import {
    INTEGRATION_COPY_MESSAGE_CONFIG_PL,
    INTEGRATION_SHOWMORE_TEXT_PL,
} from '@/static/content/pl/integration/integration';
import {
    INTEGRATION_COPY_MESSAGE_CONFIG_RO,
    INTEGRATION_SHOWMORE_TEXT_RO,
} from '@/static/content/ro/integration/integration';

import { env } from '@/env.mjs';

type IntegrationItemStepsType = {
    number: string;
    title: string;
    subtitle: string;
    code?: string;
    buttonText?: string;
};

export interface IntegrationStepsProps {
    integrationSteps: IntegrationItemStepsType[];
}
const configs = {
    RO: {
        toast: INTEGRATION_COPY_MESSAGE_CONFIG_RO,
        showMore: INTEGRATION_SHOWMORE_TEXT_RO,
    },
    PL: {
        toast: INTEGRATION_COPY_MESSAGE_CONFIG_PL,
        showMore: INTEGRATION_SHOWMORE_TEXT_PL,
    },
};

const IntegrationSteps: FC<IntegrationStepsProps> = ({ integrationSteps }) => {
    const country = env.NEXT_PUBLIC_APP_COUNTRY as 'RO' | 'PL';

    const configToast = configs[country].toast;
    const configShowMore = configs[country].showMore;

    return (
        <ol>
            {integrationSteps.map((step, i) => (
                <li key={`integration-step-${i}`}>
                    <div className="mb-4 flex items-center">
                        <span className="text-typography-primary relative z-auto flex h-[48px] w-[48px] items-center justify-center rounded-full bg-green-100 font-extrabold leading-none">
                            {step.number}
                        </span>

                        <Title
                            size="m"
                            className="w-auto pl-4"
                        >
                            {step.title}
                        </Title>
                    </div>

                    <P
                        size="s"
                        className="mb-4"
                    >
                        {step.subtitle}
                    </P>

                    {step.code && (
                        <Card
                            size={'m'}
                            className="mb-6 w-full"
                        >
                            <Button
                                size="s"
                                variant="outline"
                                data-test-id="integration-copy-code-button"
                                className="mb-3 ml-auto block whitespace-nowrap"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(
                                            step.code ?? '',
                                        );

                                        useToast(
                                            configToast.success as ToastProps,
                                        );
                                    } catch (error) {
                                        useToast(
                                            configToast.error as ToastProps,
                                        );
                                    }
                                }}
                            >
                                {step.buttonText}
                            </Button>

                            <ShowMoreContent
                                lines={20}
                                showMoreLabel={configShowMore.showMoreLabel}
                                showLessLabel={configShowMore.showLessLabel}
                                buttonClassName={buttonVariants({
                                    variant: 'ghost',
                                })}
                            >
                                <code className="text-typography-secondary text-xxs mr-4 whitespace-break-spaces font-mono">
                                    {step.code}
                                </code>{' '}
                            </ShowMoreContent>
                        </Card>
                    )}
                </li>
            ))}
        </ol>
    );
};

export default IntegrationSteps;
